<script>
import {
  iIcon,
  iToggle,
  IToggleSizes,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import { mapGetters } from 'vuex';
import { TrustScoreDistributionDataSets } from '@/V2/stores/SimilarDocumentsStore';
import ScaledBar from '@/V2/components/charts/ScaledBar';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import palette from '@inscribe/signal/dist/palette';

import {
  Chart,
  LinearScale,
  LineController,
  CategoryScale,
  PointElement,
  LineElement,
  BarController,
  BarElement,
} from 'chart.js';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iIcon,
    iToggle,
    iTooltip,
    ScaledBar,
  },

  props: {
    document: {
      type: Object,
      default: undefined,
    },
    customerId: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    IToggleSizes,
    iTooltipAlignmentMode,
    selectedDataset: null,
    canvasId: `canvas-${new Date().getTime()}`,
    palette,
    externalLink:
      'https://help.inscribe.ai/en/articles/8372845-trust-score-distribution',
  }),

  computed: {
    ...mapGetters({
      getScoreDistribution: 'getScoreDistribution',
      hasWorskpaceData: 'hasWorskpaceData',
      hasInscribeData: 'hasInscribeData',
    }),
    shouldDisplayChart() {
      return this.hasWorskpaceData || this.hasInscribeData;
    },
    toggleValue() {
      return this.selectedDataset === TrustScoreDistributionDataSets.WORKSPACE;
    },
    legendText() {
      return this.selectedDataset === TrustScoreDistributionDataSets.WORKSPACE
        ? 'Similar documents in this workspace'
        : 'Similar documents seen by Inscribe';
    },
    chartColor() {
      return this.selectedDataset === TrustScoreDistributionDataSets.WORKSPACE
        ? '#0091FF'
        : '#8E4EC6';
    },
  },

  mounted() {
    if (this.shouldDisplayChart) {
      if (this.hasWorskpaceData) {
        this.selectedDataset = TrustScoreDistributionDataSets.WORKSPACE;
      } else if (this.hasInscribeData) {
        this.selectedDataset = TrustScoreDistributionDataSets.INSCRIBE;
      }
      this.drawChart();

      // position indicator
      const docIndicatorEl = this.$refs.docIndicator;
      if (docIndicatorEl) {
        docIndicatorEl.style.left = `${this.document.trust_score}%`;
      }
    }
  },

  methods: {
    onChartToggle: function (selected) {
      this.selectedDataset = selected
        ? TrustScoreDistributionDataSets.WORKSPACE
        : TrustScoreDistributionDataSets.INSCRIBE;
      this.drawChart();
    },

    drawChart() {
      if (this.currentChart) {
        this.currentChart.destroy();
      }
      const ctx = document.getElementById(this.canvasId);

      if (ctx) {
        const context = ctx.getContext('2d');
        const data = this.getScoreDistribution(this.selectedDataset);
        const barData = data[this.selectedDataset];

        Chart.register(LinearScale);
        Chart.register(LineElement);
        Chart.register(LineController);
        Chart.register(CategoryScale);
        Chart.register(PointElement);
        Chart.register(BarController);
        Chart.register(BarElement);

        /* eslint-disable no-new */
        /* eslint-disable no-undef */
        Chart.defaults.plugins.legend = false;
        this.currentChart = new Chart(context, {
          data: {
            labels: barData.map((a) => a),
            datasets: [
              {
                type: 'bar',
                label: 'Workspace',
                data: barData,
                backgroundColor: this.chartColor,
                borderRadius: 0,
                width: 5,
                borderColor: this.chartColor,
                borderWidth: 1,
                barPercentage: 0.5,
                inflateAmount: 0.2,
              },
            ],
          },
          options: {
            plugins: {
              tooltip: {
                enabled: false,
              },
            },
            scales: {
              x: {
                display: false,
                grid: {
                  display: false,
                },
              },
              y: {
                display: false,
                grid: {
                  display: false,
                },
              },
            },
          },
        });
      }
    },
  },
};
</script>

<template>
  <div class="document-trust-score-chart-wrapper">
    <!-- empty state -->
    <document-right-panel-empty-state
      v-if="!hasWorskpaceData && !hasInscribeData"
      data-testid="trust-score-chart-empty-state"
      symbol="medium-graph-2"
      title="Trust score distribution not available"
      sub-title="There are not enough similar documents to compare to"
      class="empty-state"
    ></document-right-panel-empty-state>

    <!-- title -->
    <div
      v-else
      class="document-trust-score-chart d-flex flex-column i-radius-3xl i-p-9"
    >
      <div
        class="trust-score-heading d-flex flex-column align-items-start i-gap-1"
      >
        <div
          class="title d-flex flex-row i-text-button-3 i-text-neutral-120 align-items-center i-gap-3"
        >
          <div class="">Trust Score Distribution</div>
          <!-- info icon -->
          <i-icon
            symbol="small-question-mark"
            :size="12"
            :colour="palette['light-neutral-80']"
            class="trust-score-distribution-help-icon"
          ></i-icon>
          <i-tooltip
            :target="`.trust-score-distribution-help-icon`"
            title="Trust Score Distribution"
            description="This graph provides context for a document's trust score. It can help you understand if a score is typical or unusual compared to similar documents."
            link-text="Learn more"
            link-external-url="externalLink"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
          />
        </div>
        <div class="subtitle i-text-body-3 i-text-neutral-110">
          Trend of trust scores for similar documents
        </div>
      </div>

      <!-- toggle container -->
      <div
        v-if="hasWorskpaceData && hasInscribeData"
        class="toggle-container i-mt-10 i-mb-2"
        data-testid="trust-score-chart-toggle"
      >
        <i-toggle
          left-label="Workspace"
          right-label="Inscribe"
          :toggle-value="toggleValue"
          :full-width="true"
          :size="IToggleSizes.SMALL"
          @value-update="onChartToggle"
        ></i-toggle>
      </div>

      <!-- chart container -->
      <div
        v-if="shouldDisplayChart"
        class="chart-container d-flex flex-column align-items-start justify-content-end i-mt-10"
      >
        <canvas
          :id="canvasId"
          height="120"
          data-testid="trust-score-distribution-chart"
        ></canvas>
        <scaled-bar :with-lines="false" :with-top-border="true"></scaled-bar>
        <div
          ref="docIndicator"
          class="indicator d-flex flex-row align-items-start justify-content-center"
        >
          <div class="dot">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="30"
              viewBox="0 0 24 30"
              fill="none"
            >
              <path
                d="M24 11.9277C24 22.4096 12 30 12 30C12 30 0 22.4096 0 11.9277C0 5.34022 5.37258 0 12 0C18.6274 0 24 5.34022 24 11.9277Z"
                fill="#151718"
              />
            </svg>
            <i-icon
              symbol="small-file"
              class="file-icon i-text-neutral-default"
              :size="12"
            ></i-icon>
          </div>
          <div class="line"></div>
        </div>
        <div
          :class="{
            workspace: selectedDataset === 'workspace',
            inscribe: selectedDataset === 'inscribe',
          }"
          class="legend-container d-flex flex-row align-items-center i-mt-10"
        >
          <div class="circle i-mr-2"></div>
          <div
            class="text i-text-body-3 i-text-neutral-120"
            data-testid="trust-score-chart-legend-text"
          >
            {{ legendText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.document-trust-score-chart {
  width: 100%;
  border: 1px solid $light-neutral-60;

  .trust-score-distribution-help-icon {
    cursor: pointer;
  }

  .chart-container {
    height: 188px;
    position: relative;

    canvas {
      max-height: 120px;
    }
    .indicator {
      position: absolute;
      left: 4px;
      bottom: 68px;
      height: 126px;
      width: 40px;
      transform-origin: center;
      transform: translateX(-50%);

      .line {
        box-sizing: content-box;
        position: absolute;
        left: 0;
        width: 50%;
        height: 100%;
        border-right: 1px dotted $light-neutral-60;
        z-index: 0;
      }

      .dot {
        z-index: 1;
        background: transparent;
        margin-left: 1px;

        .file-icon {
          position: absolute;
          top: 13px;
          left: 52%;
          transform-origin: center;
          transform: translate(-50%, -50%);
        }
      }
    }
  }

  .legend-container {
    height: 20px;

    .circle {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #0091ff; // todo export from signal
    }

    &.inscribe .circle {
      background: #8e4ec6; // todo export from signal
    }
  }
}
</style>
